import React from "react"
import { useSelectLocation } from "./useSelectLocation"
import { useQueryParamsSync } from "./useQueryParamSync"
import { useInitialDataFetch } from "./useInitialDataFetch"
import { dataStatusesError, dataStatusesLoading } from "../shared/utils"
import { useNavigationOrchestrator } from "./useNavigationOrchestrator"
import { InternalError } from "../../pages/InternalError"
import { useLocation } from "react-router"
import { publicRoutes } from "../../routes"
import { DataStatus } from "../../domain/enums"
import { useFetchDetachedProviders } from "./useFetchDetachedProviders"
import Loading from "../../pages/Loading"
import { useDeepLinkHandler } from "../shared/hooks/useDeepLinkHandler"

interface DataFetchingRouterProps {
  children?: React.ReactNode
}

export const DataFetchingRouter: React.FC<DataFetchingRouterProps> = ({
  children,
}) => {
  const location = useLocation()
  const publicRoute = publicRoutes.includes(location.pathname)

  // initial needed data fetch
  const initialDataFetchDataStatus = useInitialDataFetch()
  const initialDataLoadingComplete =
    initialDataFetchDataStatus === DataStatus.LOADED

  // Deep link handler state - gated on initial data fetch completion
  const { complete: deepLinkHandlerComplete, finalUrl } = useDeepLinkHandler(
    initialDataLoadingComplete,
  )

  // Only run navigation orchestrator after deep link is handled
  // Pass the full finalizedDeepLinkUrl (with query params) to orchestrator
  const { complete: orchestratorComplete } = useNavigationOrchestrator(
    deepLinkHandlerComplete,
    finalUrl || undefined,
  )

  useSelectLocation()
  useQueryParamsSync()
  useFetchDetachedProviders()

  const initialDataLoading = dataStatusesLoading([initialDataFetchDataStatus])
  const initialDataError = dataStatusesError([initialDataFetchDataStatus])

  if (
    (initialDataLoading && !publicRoute) ||
    !initialDataLoadingComplete ||
    !deepLinkHandlerComplete ||
    (!orchestratorComplete && !publicRoute)
  ) {
    return <Loading />
  }

  if (initialDataError) {
    return <InternalError />
  }

  return <>{children}</>
}
